import {Box, Button, ButtonGroup, Heading, type ButtonProps, Octicon} from '@primer/react'
import {Link} from '@github-ui/react-core/link'
import {addOrModifyUrlParameters} from '../../utils/add-or-modify-url-parameters'
import type {PaginationCursor} from '../../types/commits-types'
import {ChevronLeftIcon, ChevronRightIcon} from '@primer/octicons-react'

interface PaginationProps {
  paginationInfo: PaginationCursor
}

export function Pagination({paginationInfo}: PaginationProps) {
  const hasPreviousPage = paginationInfo.hasPreviousPage
  const hasNextPage = paginationInfo.hasNextPage

  const getDisabledProps = (disabled: boolean) => {
    const ariaDisabledProps: Pick<ButtonProps, 'className' | 'onClick' | 'aria-disabled' | 'sx'> = disabled
      ? {
          'aria-disabled': true,
          sx: {
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'default',
            color: 'primer.fg.disabled',
            fontWeight: 'normal',
          },
          onClick: e => e.preventDefault(),
        }
      : {}

    return ariaDisabledProps
  }

  // If there's only one page, don't show pagination
  if (!hasPreviousPage && !hasNextPage) {
    return null
  }

  return (
    <>
      <Heading as="h2" className="sr-only">
        Pagination
      </Heading>

      <Box sx={{display: 'flex', justifyContent: 'center', mt: 2}}>
        <ButtonGroup>
          <Button
            as={Link}
            to={generateCommitPaginationUrl(paginationInfo, false)}
            sx={{color: 'accent.fg', fontWeight: 'normal'}}
            {...getDisabledProps(!hasPreviousPage)}
            tabIndex={0}
            variant="invisible"
            data-testid="pagination-prev-button"
            leadingVisual={hasPreviousPage ? () => <Octicon icon={ChevronLeftIcon} sx={{color: 'accent.fg'}} /> : null}
          >
            Previous
          </Button>
          <Button
            as={Link}
            to={generateCommitPaginationUrl(paginationInfo, true)}
            sx={{color: 'accent.fg', fontWeight: 'normal'}}
            {...getDisabledProps(!hasNextPage)}
            tabIndex={0}
            variant="invisible"
            data-testid="pagination-next-button"
            trailingVisual={hasNextPage ? () => <Octicon icon={ChevronRightIcon} sx={{color: 'accent.fg'}} /> : null}
          >
            Next
          </Button>
        </ButtonGroup>
      </Box>
    </>
  )
}

function generateCommitPaginationUrl(paginationInfo: PaginationCursor, isNext: boolean) {
  let urlRecord: Record<string, string>
  if (isNext) {
    urlRecord = {after: paginationInfo.endCursor}
  } else {
    urlRecord = {before: paginationInfo.startCursor}
  }
  return addOrModifyUrlParameters(urlRecord, [isNext ? 'before' : 'after'])
}

try{ Pagination.displayName ||= 'Pagination' } catch {}